import { PdpImageCarousel } from "./PdpImageCarousel";

import { useState } from "react";
import type { Swiper as SwiperClass } from "swiper";
import { Pagination, Thumbs } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { useTranslation } from "@/core/i18n";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/thumbs";

interface ImageSlide {
  src: string;
  alt: string;
}

export interface ImageCarouselProps {
  slides: ImageSlide[];
  productName?: string;
  presentation?: "pdp";
  frameClassName?: string;
}

export const ImageCarousel = (props: ImageCarouselProps) =>
  props.presentation === "pdp" ? (
    <PdpImageCarousel {...props} />
  ) : (
    <DefaultImageCarousel {...props} />
  );

const DefaultImageCarousel = ({ slides, productName }: ImageCarouselProps) => {
  const { t } = useTranslation();
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null);
  const hasSlides = slides && slides.length > 0;
  if (!hasSlides) {
    return null;
  }

  const pagination = {
    clickable: true,
    dynamicBullets: true,
  };

  const hasThumbnails = slides.length > 1;
  const altText = (slide: ImageSlide) =>
    slide.alt || productName || t("imageCarousel.productImage");

  return (
    <div className="flex w-full flex-col items-center desk:items-stretch">
      <Swiper
        className="relative h-[320px] w-[300px] overflow-hidden rounded-lg desk:h-auto desk:w-full desk:pb-0 [&_.swiper-pagination-bullet]:!size-[10px] [&_.swiper-pagination-bullet]:!bg-brand-color [&_.swiper-pagination]:!bottom-0 desk:[&_.swiper-pagination]:!hidden"
        pagination={pagination}
        thumbs={hasThumbnails ? { swiper: thumbsSwiper } : undefined}
        modules={[Pagination, Thumbs]}
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={`${slide.src}-${index}`}>
            <img
              src={slide.src}
              alt={altText(slide)}
              className="aspect-square size-[300px] rounded-lg object-cover desk:size-auto desk:w-full"
            />
          </SwiperSlide>
        ))}
      </Swiper>

      {hasThumbnails && (
        <Swiper
          onSwiper={setThumbsSwiper}
          slidesPerView={5}
          spaceBetween={12}
          watchSlidesProgress
          modules={[Thumbs]}
          className="hidden w-full desk:mt-3 desk:block"
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={`thumb-${slide.src}-${index}`}>
              <img
                src={slide.src}
                alt=""
                aria-hidden
                className="aspect-square w-full cursor-pointer rounded-md object-cover opacity-50 transition-opacity hover:opacity-80 [.swiper-slide-thumb-active_&]:opacity-100 [.swiper-slide-thumb-active_&]:ring-2 [.swiper-slide-thumb-active_&]:ring-brand-color"
              />
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </div>
  );
};
